/**
 * THE ORIGINAL PHOTO, AND THE AI ANALYSIS COPY.
 *
 * These are two different things and the whole file exists to keep them apart.
 *
 *   THE ORIGINAL PHOTO   doc.photo. What the customer's house looks like, and
 *                        the only thing the canvas, the preview, saving and
 *                        export ever touch. Nothing here writes to it, returns
 *                        a replacement for it, or is stored anywhere near it.
 *
 *   THE AI ANALYSIS COPY A transient, smaller re-encoding made ONLY when the
 *                        original is too large to send, used ONLY as the input
 *                        to roof analysis, and thrown away when the request
 *                        ends. It never reaches the canvas and is never saved.
 *
 * WHY A COPY IS NEEDED AT ALL. Netlify buffers a request at 6 MB and base64
 * encodes binary bodies first, so about 4 MB of image is all that can be sent.
 * A detailed photograph can exceed that, and the previous behavior was to
 * refuse it - which reads to a contractor as "this app does not work on my
 * photos".
 *
 * WHY THE COORDINATES STILL LINE UP. Proposals come back in NORMALIZED 0..1
 * coordinates, against whatever image was analyzed. A copy that preserves the
 * aspect ratio therefore maps onto the original with no transform at all: 0.42
 * of the way across a 1600px copy is 0.42 of the way across the 4000px
 * original. This is not a lucky accident, it is why the coordinate system was
 * normalized in the first place, and analysisCopyFor refuses to return a copy
 * whose aspect ratio drifted far enough to matter.
 */

/** The original, exactly as the document holds it. Never modified here. */
export interface OriginalPhoto {
  dataUrl: string
  width: number
  height: number
}

/**
 * What actually gets sent for analysis. `fromOriginal: true` means these ARE
 * the original's bytes, untouched - the common case, and the one where there
 * is nothing to measure because nothing changed.
 */
export interface AnalysisCopy {
  bytes: Uint8Array<ArrayBuffer>
  type: string
  width: number
  height: number
  /** 1 when the original was sent as-is. */
  scale: number
  fromOriginal: boolean
  /** For the operational record and for honest reporting. */
  originalBytes: number
  originalWidth: number
  originalHeight: number
}

/* ------------------------------------------------------------- the numbers */

/** The hard transport ceiling. Must match the server's MAX_IMAGE_BYTES. */
export const UPLOAD_LIMIT_BYTES = 4_000_000

/**
 * What a resize aims for: comfortably below the limit rather than just under
 * it. JPEG size is not perfectly predictable from pixel count, so a target at
 * the ceiling would need several attempts on every photograph and would
 * sometimes land at 3.99 MB. 3 MB leaves 25% of room.
 */
export const ANALYSIS_TARGET_BYTES = 3_000_000

/**
 * Quality for the analysis copy. High on purpose: this is the image a model
 * reads a roof line off, and JPEG artefacts along a high-contrast fascia are
 * exactly the thing that would cost detail. Higher than the 0.95 used on
 * import would not survive the size budget on the photographs that need this.
 */
export const ANALYSIS_QUALITY = 0.92

/**
 * A FLOOR, BELOW WHICH THIS REFUSES RATHER THAN SHIPS MUSH.
 *
 * There is a size past which downscaling stops being a transport concession
 * and starts being a different, worse photograph. If a picture cannot be got
 * under the limit with its long edge at 1600px, the honest answer is to say so
 * and let the person supply a smaller file, not to send something no model
 * could read a roof off and call it a suggestion.
 */
export const MIN_LONG_EDGE = 1600

/** Attempts before giving up. Each is one canvas encode. */
const MAX_ATTEMPTS = 5

/**
 * The largest normalized coordinate error the integer rounding of width and
 * height may introduce. 1/2000 of the frame - a fifth of a pixel on a 4000px
 * photograph - which is far below the width of a roof line.
 */
export const MAX_ASPECT_DRIFT = 0.0005

export class AnalysisCopyError extends Error {}

/* ---------------------------------------------------------------- encoding */

export interface Encoder {
  (original: OriginalPhoto, width: number, height: number, quality: number): Promise<{
    bytes: Uint8Array<ArrayBuffer>
    type: string
  }>
}

const pack = (chars: string) => {
  const out = new Uint8Array(new ArrayBuffer(chars.length))
  for (let i = 0; i < chars.length; i++) out[i] = chars.charCodeAt(i)
  return out
}

/** Decode a data URL to bytes. Read-only; the original is never rewritten. */
export function bytesOf(dataUrl: string): { bytes: Uint8Array<ArrayBuffer>; type: string } | null {
  const m = /^data:([^;,]+)(;base64)?,(.*)$/s.exec(dataUrl)
  if (!m) return null
  try {
    return { bytes: pack(m[2] ? atob(m[3]) : decodeURIComponent(m[3])), type: m[1].toLowerCase() }
  } catch {
    return null
  }
}

/**
 * The browser encoder. A canvas, drawn once at the target size with the best
 * resampling the browser offers, and read back as JPEG.
 *
 * NO CROPPING. The whole image is drawn into the whole canvas, so nothing is
 * cut off any edge - a cropped analysis copy would produce suggestions for a
 * roof line that is not where the model said it was.
 *
 * ORIENTATION IS ALREADY SETTLED. doc.photo was produced by loadPhotoFile,
 * which draws the decoded image into a canvas, so EXIF rotation has already
 * been applied to the pixels. Drawing those pixels again cannot change it.
 */
export const canvasEncoder: Encoder = async (original, width, height, quality) => {
  const img = await new Promise<HTMLImageElement>((resolve, reject) => {
    const i = new Image()
    i.onload = () => resolve(i)
    i.onerror = () => reject(new AnalysisCopyError('The photo could not be read for analysis.'))
    i.src = original.dataUrl
  })
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new AnalysisCopyError('This browser could not prepare the photo for analysis.')
  ctx.imageSmoothingEnabled = true
  ctx.imageSmoothingQuality = 'high'
  // Source rect omitted entirely: the whole image, into the whole canvas.
  ctx.drawImage(img, 0, 0, width, height)

  const url = canvas.toDataURL('image/jpeg', quality)
  // A failed or out-of-memory encode returns "data:," or "", and both look
  // like a photograph to everything downstream. Check rather than trust.
  if (!url.startsWith('data:image/jpeg') || url.length < 1000) {
    throw new AnalysisCopyError('This browser could not prepare the photo for analysis.')
  }
  const decoded = bytesOf(url)
  if (!decoded) throw new AnalysisCopyError('This browser could not prepare the photo for analysis.')
  return decoded
}

/* ------------------------------------------------------------------ the copy */

/**
 * Produce the image to analyze. Returns the ORIGINAL's own bytes whenever they
 * fit, and a smaller re-encoding only when they do not.
 *
 * The original photo is an input to this function and never an output of it.
 */
export async function analysisCopyFor(
  original: OriginalPhoto,
  opts: {
    encode?: Encoder
    limitBytes?: number
    targetBytes?: number
    minLongEdge?: number
  } = {},
): Promise<AnalysisCopy> {
  const limit = opts.limitBytes ?? UPLOAD_LIMIT_BYTES
  const target = Math.min(opts.targetBytes ?? ANALYSIS_TARGET_BYTES, limit)
  const minEdge = opts.minLongEdge ?? MIN_LONG_EDGE
  const encode = opts.encode ?? canvasEncoder

  const source = bytesOf(original.dataUrl)
  if (!source || source.bytes.byteLength === 0) {
    throw new AnalysisCopyError('Add a photo first, then press Generate.')
  }

  const base = {
    originalBytes: source.bytes.byteLength,
    originalWidth: original.width,
    originalHeight: original.height,
  }

  /*
   * THE COMMON CASE, AND THE IMPORTANT ONE: it already fits, so it is sent
   * exactly as it is. No re-encode, no generation loss, nothing to measure -
   * a photograph that fits is bit-for-bit the photograph the model reads.
   */
  if (source.bytes.byteLength <= limit) {
    return {
      bytes: source.bytes,
      type: source.type,
      width: original.width,
      height: original.height,
      scale: 1,
      fromOriginal: true,
      ...base,
    }
  }

  /*
   * Too large. JPEG size runs roughly with pixel count, so the first guess is
   * the square root of the ratio; the loop is there because "roughly" is not
   * "exactly" and a detailed photograph compresses worse than the estimate.
   */
  const longEdge = Math.max(original.width, original.height)
  let scale = Math.min(1, Math.sqrt(target / source.bytes.byteLength))

  for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
    if (longEdge * scale < minEdge) {
      // Only refuse if even the floor cannot fit; try the floor exactly first.
      scale = minEdge / longEdge
      if (scale >= 1) break
    }
    const width = Math.max(1, Math.round(original.width * scale))
    const height = Math.max(1, Math.round(original.height * scale))

    const out = await encode(original, width, height, ANALYSIS_QUALITY)
    if (out.bytes.byteLength <= limit) {
      assertAspect(original, width, height)
      return {
        bytes: out.bytes,
        type: out.type,
        width,
        height,
        scale,
        fromOriginal: false,
        ...base,
      }
    }
    if (Math.max(width, height) <= minEdge) break
    // Still too big. Take another 15% off the linear dimension.
    scale *= 0.85
  }

  throw new AnalysisCopyError(
    'That photo is too detailed to analyze. Take it again at a smaller size, or crop it yourself, and try Generate once more.',
  )
}

/**
 * THE PROPERTY THE COORDINATES DEPEND ON.
 *
 * Proposals arrive normalized against whatever image was analyzed, so they map
 * onto the original for free - provided the copy has the same shape. Integer
 * width and height introduce a tiny drift; this bounds it, and throws rather
 * than returning a copy whose suggestions would sit off the roof line.
 */
function assertAspect(original: OriginalPhoto, width: number, height: number) {
  const wanted = original.width / original.height
  const got = width / height
  // Worst case normalized error: a point at the far edge of the frame.
  const drift = Math.abs(got - wanted) / wanted
  if (drift > MAX_ASPECT_DRIFT) {
    throw new AnalysisCopyError('The photo could not be prepared for analysis without distorting it.')
  }
}

/**
 * Map a normalized point from the analysis copy back onto the original.
 *
 * It is the identity function, and it exists to say so in one place: this is
 * the whole of the coordinate mapping, there is no scale factor to remember,
 * and anything that ever needs one has broken the aspect-ratio invariant
 * above. Used by the tests that prove it.
 */
export const toOriginalCoords = <T extends { x: number; y: number }>(p: T): T => p
